import { useEffect, useState } from 'react';

import User from '@/assets/user.svg?react';

import { useFormDataQuery } from '@/api/getters';
import { BUSINESS_TYPE } from '@/api/types';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { Hint } from '@/components/ui/Hint';
import InfoToggle from '@/components/ui/InfoToggle';
import { Input } from '@/components/ui/Input';
import { InputMask } from '@/components/ui/InputMask';

import { getBankNameByIban } from '../helpers/iban';
import { OWNER_COMPANY_NAME } from '../surveyTwo/Forms/CompaniesCompanyInputs';
import {
  validateBankAccountNumber,
  validateCompanyName,
  validateFirstName,
  validateLastName,
} from '../Validate';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const OWNER_ACCOUNT_NUMBER_NAME = 'owner.account_number';
export const OWNER_ACCOUNT_OWNER_FIRST_NAME = 'owner.account_owner_first_name';
export const OWNER_ACCOUNT_OWNER_LAST_NAME = 'owner.account_owner_last_name';

const BankInfoInputs = () => {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();

  const businessType = data?.offer.business_type ?? null;
  const displayCompanyName = businessType && businessType !== BUSINESS_TYPE.JDG;

  const { trigger, watch } = useFormContext();

  const [focused, setFocused] = useState('');
  const [onPaste, setOnPaste] = useState(false);

  useEffect(() => {
    if (data?.payload?.data?.owner?.account_number?.length > 0) {
      trigger(OWNER_ACCOUNT_NUMBER_NAME);
    }
    if (data?.payload?.data?.owner?.account_owner_first_name?.length > 0) {
      trigger(OWNER_ACCOUNT_OWNER_FIRST_NAME);
    }
    if (data?.payload?.data?.owner?.account_owner_last_name?.length > 0) {
      trigger(OWNER_ACCOUNT_OWNER_LAST_NAME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="md:col-span-4">
        <FormField
          name={OWNER_ACCOUNT_NUMBER_NAME}
          defaultValue={''}
          rules={{
            validate: validateBankAccountNumber,
          }}
          render={({ field, fieldState }) => {
            const { value, onChange, onBlur, ...restField } = field;
            return (
              <FormItem>
                <FormControl>
                  <InputMask
                    mask="00 0000 0000 0000 0000 0000 0000"
                    value={value}
                    label={i18n.t('Numer rachunku bankowego')}
                    onPaste={(e) => {
                      if (
                        e.clipboardData.getData('text').replaceAll(' ', '')
                          .length !== 26
                      ) {
                        onChange(
                          e.clipboardData.getData('text').replaceAll(' ', '')
                        );
                        return;
                      }

                      setOnPaste(true);
                      onChange(
                        e.clipboardData.getData('text').replaceAll(' ', '')
                      );
                      if (fieldState.error) {
                        trigger(field.name);
                      }
                    }}
                    onBlur={() => {
                      setFocused('');
                      onBlur();
                    }}
                    onAccept={(e, _, target) => {
                      if (target?.inputType === 'insertFromPaste') {
                        return;
                      }
                      setOnPaste(false);

                      if (e === null) {
                        onChange('');
                        return;
                      }

                      onChange(e.replaceAll(' ', ''));
                      if (fieldState.error) {
                        trigger(field.name);
                      }
                    }}
                    onFocus={() => setFocused(field.name)}
                    error={fieldState.error?.message}
                    success={
                      focused !== field.name && !fieldState.error && field.value
                    }
                    {...restField}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
        <Hint className="mt-2">
          {i18n.t(
            'Podaj dane i numer rachunku bankowego właściciela lub współwłaściciela pojazdu.'
          )}
        </Hint>
        {onPaste && (
          <InfoToggle
            info={i18n.t('Sprawdź, czy skopiowany numer jest poprawny')}
          />
        )}
        {validateBankAccountNumber(watch(OWNER_ACCOUNT_NUMBER_NAME)) === true &&
          getBankNameByIban(watch(OWNER_ACCOUNT_NUMBER_NAME)) && (
            <p className="caption mt-2 rounded-md bg-green-500 px-2.5 py-2 text-gray-100">
              {i18n.t('Wykonamy przelew do banku')}{' '}
              {getBankNameByIban(watch(OWNER_ACCOUNT_NUMBER_NAME))}
            </p>
          )}
      </div>
      <p className="h5 mt-2 flex items-center gap-2 md:col-span-4">
        <User className="size-4 flex-none text-blue-600" />
        {i18n.t(
          displayCompanyName
            ? 'Podaj nazwę spółki'
            : 'Podaj imię i nazwisko właściciela rachunku bankowego'
        )}
      </p>
      {!displayCompanyName && (
        <>
          <FormField
            name={OWNER_ACCOUNT_OWNER_FIRST_NAME}
            defaultValue={''}
            rules={{
              validate: (value) => validateFirstName(value),
            }}
            render={({ field, fieldState }) => {
              const { value, onChange, onBlur, ...restField } = field;
              return (
                <FormItem className="md:col-span-1">
                  <FormControl>
                    <Input
                      value={value === null ? '' : value}
                      label={i18n.t('Imię')}
                      error={fieldState.error?.message}
                      success={
                        focused !== field.name &&
                        !fieldState.error &&
                        field.value
                      }
                      onChange={(e) => {
                        onChange(e);
                        if (fieldState.error) {
                          trigger(field.name);
                        }
                      }}
                      onBlur={() => {
                        setFocused('');
                        onBlur();
                      }}
                      onFocus={() => setFocused(field.name)}
                      {...restField}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          <FormField
            name={OWNER_ACCOUNT_OWNER_LAST_NAME}
            rules={{
              validate: (value) => validateLastName(value),
            }}
            render={({ field, fieldState }) => {
              const { value, onChange, onBlur, ...restField } = field;
              return (
                <FormItem className="md:col-span-3">
                  <FormControl>
                    <Input
                      value={value === null ? '' : value}
                      label={i18n.t('Nazwisko')}
                      error={fieldState.error?.message}
                      success={
                        focused !== field.name &&
                        !fieldState.error &&
                        field.value
                      }
                      onChange={(e) => {
                        onChange(e);
                        if (fieldState.error) {
                          trigger(field.name);
                        }
                      }}
                      onBlur={() => {
                        setFocused('');
                        onBlur();
                      }}
                      onFocus={() => setFocused(field.name)}
                      {...restField}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </>
      )}
      {displayCompanyName && (
        <FormField
          name={OWNER_COMPANY_NAME}
          rules={{
            validate: (value) => validateCompanyName(value),
          }}
          render={({ field, fieldState }) => {
            const { onChange, onBlur, ...restField } = field;
            return (
              <FormItem className="md:col-span-4">
                <FormControl>
                  <Input
                    label={i18n.t('Podaj nazwę spółki')}
                    error={fieldState.error?.message}
                    success={
                      focused !== field.name && !fieldState.error && field.value
                    }
                    onChange={(e) => {
                      onChange(e);
                      if (fieldState.error) {
                        trigger(field.name);
                      }
                    }}
                    onBlur={() => {
                      setFocused('');
                      onBlur();
                    }}
                    onFocus={() => setFocused(field.name)}
                    {...restField}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
      )}
    </>
  );
};

export default BankInfoInputs;
